import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLangue, useTextes } from '../LangueContexte'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { CarteRecherche } from './CarteDifferee'
import { Vignette, VignetteSquelette } from './Vignette'
import { EnregistrerRecherche } from './Suivi'
import { PrevenezMoi } from './FeuilleVille'
import { libelleVille, useVille } from '../VilleContexte'
import { Bouton, EtatVide, Icone, Modale, Puce } from '../ui'
import { montant, nombre } from '../formats'
import { ErreurApi } from '../api'
import { interpoler, messageErreur, type Langue } from '../i18n'
import { EQUIPEMENTS } from '../equipements'
import { useRecommandees } from '../useRecommandees'
import {
  ajouterFavori,
  listerAncres,
  lireOptionsRecherche,
  listerQuartiers,
  rechercherAuCurseur,
  rechercherPunaises,
  retirerFavori,
  type Ancre,
  type Punaise,
  type Vignette as VignetteDto,
  type Quartier,
  type Ville,
} from '../apiRecherche'
import {
  aDesFiltres,
  aUnCadrage,
  ecrireCriteres,
  lireCriteres,
  pourUneAutreVille,
  type Criteres,
  type Tri,
} from '../criteres'

/**
 * Au-delà de cette largeur, la carte est montrée par défaut.
 *
 * C'est le point de rupture « large » du §3 des jetons, écrit ici en clair parce qu'une variable
 * CSS ne se lit pas depuis `matchMedia`. Il ne s'agit pas d'un réglage : c'est la largeur en
 * dessous de laquelle deux colonnes ne tiennent plus.
 */
const LARGEUR_DEUX_COLONNES = '(min-width: 60rem)'

/**
 * Six résultats par page, et six de plus à chaque « voir plus » — ticket `fo-recherche-11`.
 *
 * **C'est la taille de page, pas le mécanisme, qui change.** La pagination reste au curseur : le
 * choix de L9 tenait à ce qu'un OFFSET décale toutes les pages suivantes dès qu'une annonce est
 * publiée pendant la consultation — un résultat déjà vu réapparaît pendant qu'un autre disparaît
 * sans avoir été lu. Rien de cela ne dépend du nombre de lignes par page.
 *
 * Six parce que c'est ce que montre la maquette, et parce que la carte se règle sur la hauteur de
 * la liste (ticket `fo-recherche-13`) : les deux nombres n'en font qu'un.
 */
const PAR_PAGE = 6

/**
 * La phrase à montrer quand un appel échoue.
 *
 * **Sans elle, l'écran affiche `internal`.** `ErreurApi` passe le CODE au constructeur d'`Error`,
 * donc son `message` est ce code : l'afficher tel quel met un identifiant technique sous les yeux
 * de quelqu'un venu chercher un logement, et lui laisse croire qu'il a fait quelque chose de mal.
 *
 * Constaté le 09/09/2026, l'API locale étant arrêtée. La panne était réelle, mais ce qu'elle
 * révélait ne l'était pas moins : cet écran était le seul à ne pas traduire ses erreurs, alors
 * qu'`ERREURS` porte les 49 codes dans les trois langues. Un code inconnu retombe sur le message
 * générique, donc il n'y a rien à tenir à jour.
 */
function phraseDErreur(cause: unknown, langue: Langue): string {
  return cause instanceof ErreurApi
    ? messageErreur(langue, cause.code, cause.reessayerDans)
    : messageErreur(langue, 'internal')
}

/**
 * Écran de recherche — carte et liste, synchronisées par l'URL.
 *
 * **L'URL est l'état.** Les filtres, le tri, la page et le cadrage vivent dans la query string, pas
 * dans un `useState` : c'est ce qui rend une recherche partageable, ce que `docs/TODO.md` exige, et
 * c'est aussi ce qui fait qu'un retour arrière restitue exactement ce qu'on avait. Un état local
 * doublant l'URL divergerait au premier bouton « précédent ».
 *
 * **L10b : les filtres passent en rangée de puces.** Un formulaire de six champs occupait le tiers
 * supérieur de l'écran le plus utilisé, avant d'avoir montré un seul résultat. Les trois filtres
 * qu'on manipule vraiment sont désormais des puces défilables ; le reste vit dans une feuille
 * qu'on ouvre. Ce n'est pas une économie de place, c'est un ordre de priorité : d'abord l'offre,
 * ensuite les réglages.
 *
 * **Recette du 08/09 — la liste ne se cache plus.** La bascule « Liste ⇄ Carte » disparaît au
 * profit d'un seul bouton « Afficher / Masquer la carte » (ticket `fo-recherche-05`) : il y a
 * toujours une liste, donc un bouton « Liste » ne mène jamais nulle part. La carte, elle, est un
 * supplément qu'on ouvre et qu'on referme — et sur mobile, ne pas l'ouvrir est ce qui évite de
 * télécharger MapLibre.
 *
 * Le paramètre d'URL reste `vue=carte` / `vue=liste`, et ce n'est pas de la paresse : L3 a fait
 * des URL de recherche des liens qu'on partage et qu'on enregistre. En changer le nom casserait
 * ceux qui existent, pour un gain purement cosmétique.
 *
 * **L13b : la ville est le périmètre, et elle vit dans l'URL comme le reste** (`city=`, D44).
 * L'URL et le contexte de ville se tiennent dans les deux sens : une URL qui porte une ville
 * l'impose au contexte — un lien partagé vers Casablanca s'ouvre à Casablanca —, et une URL qui
 * n'en porte pas reçoit la ville résolue par la chaîne de D45, en remplacement d'historique. La
 * recherche n'est interrogée qu'une fois la ville connue : sans cela, elle partirait deux fois,
 * la première dans la ville par défaut, et la liste changerait sous les yeux.
 */
export function Recherche({ connecte }: { connecte: boolean }) {
  const t = useTextes()
  const langue = useLangue()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const emplacement = useLocation()
  const ville = useVille()

  const criteres = useMemo(() => lireCriteres(params.toString()), [params])
  const tri = (params.get('sort') as Tri | null) ?? 'Relevance'

  // La ville interrogée : celle de l'URL, sinon celle du contexte. Les deux ne diffèrent que le
  // temps d'un rendu, celui de l'effet ci-dessous.
  const codeVille = criteres.cityCode?.toUpperCase() ?? ville.courante?.code
  const villeCourante = ville.trouver(codeVille) ?? null

  // Rien ne part tant que la chaîne n'a pas abouti, ni tant que la ville résolue n'est pas dans
  // l'URL : c'est ce qui évite la double requête décrite en tête de composant. Si le référentiel
  // est illisible, le contexte n'a pas de ville et l'on part sans — le serveur appliquera la
  // sienne et le dira.
  const villePrete = ville.prete && (criteres.cityCode !== undefined || ville.courante === null)

  /*
    La carte est montrée par défaut sur un grand écran et masquée sur un petit, tant que personne
    n'a tranché. Un défaut par largeur plutôt qu'un défaut unique : à deux colonnes, la maquette
    montre la carte, et sur mobile la montrer d'office ferait payer MapLibre à quelqu'un qui n'a
    peut-être jamais eu l'intention de l'ouvrir.
  */
  const vueDemandee = params.get('vue')
  const [deuxColonnes, setDeuxColonnes] = useState(
    () => globalThis.matchMedia?.(LARGEUR_DEUX_COLONNES).matches ?? true,
  )

  useEffect(() => {
    const requete = globalThis.matchMedia?.(LARGEUR_DEUX_COLONNES)

    if (requete === undefined) {
      return
    }

    const suivre = () => setDeuxColonnes(requete.matches)

    requete.addEventListener('change', suivre)

    return () => requete.removeEventListener('change', suivre)
  }, [])

  const carteVisible = vueDemandee === null ? deuxColonnes : vueDemandee === 'carte'

  useEffect(() => {
    if (!ville.prete) {
      return
    }

    if (criteres.cityCode !== undefined) {
      // L'URL dit une ville : le contexte la suit, sans la mémoriser — ce n'est pas un choix.
      if (ville.courante?.code !== criteres.cityCode.toUpperCase()) {
        ville.choisir(criteres.cityCode, 'url')
      }
      return
    }

    if (ville.courante !== null) {
      // Le tri et la vue ne sont recopiés que s'ils étaient là : l'URL d'arrivée reste aussi
      // courte qu'on l'a reçue, plus la ville.
      setParams(
        ecrireCriteres(
          { ...criteres, cityCode: ville.courante.code },
          { sort: params.get('sort') ?? undefined, vue: vueDemandee ?? undefined },
        ),
        { replace: true },
      )
    }
    // `ville` change à chaque rendu du fournisseur ; ce sont ses deux lectures qui comptent ici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ville.prete, ville.courante, criteres.cityCode])

  // Une LISTE cumulative et non une page : « voir plus » ajoute, il ne remplace pas.
  const [vignettes, setVignettes] = useState<VignetteDto[]>([])
  const [total, setTotal] = useState(0)
  const [curseur, setCurseur] = useState<string | null>(null)
  const [plafondAtteint, setPlafondAtteint] = useState(false)
  const [punaises, setPunaises] = useState<Punaise[]>([])
  const [tronque, setTronque] = useState(false)
  const [quartiers, setQuartiers] = useState<Quartier[]>([])
  // Les paliers de la puce « budget » : un réglage du back-office, lu avec les référentiels.
  const [budgets, setBudgets] = useState<number[]>([])
  const [ancres, setAncres] = useState<Ancre[]>([])
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [feuilleOuverte, setFeuilleOuverte] = useState(false)
  const [survolee, setSurvolee] = useState<string | null>(null)

  // « Recommandée pour vous » — ticket `fo-recherche-16`. Demandé APRÈS chaque page de résultats,
  // pour les seules cartes nouvelles ; la liste n'attend jamais l'étiquette. Mémorisé sur les
  // vignettes, pas recalculé à chaque survol : c'est la liste qui change, pas la question.
  const identifiants = useMemo(() => vignettes.map((annonce) => annonce.id), [vignettes])
  const recommandees = useRecommandees(identifiants, connecte)

  // Les référentiels ne dépendent d'aucun filtre — mais ils dépendent de la VILLE (L13) : les
  // quartiers de Salé ne disent rien de Casablanca, et les paliers de budget non plus. Une lecture
  // par ville, que le service worker met en cache (invariant 17).
  useEffect(() => {
    if (!villePrete) {
      return
    }

    void listerQuartiers(codeVille).then(setQuartiers).catch(() => setQuartiers([]))
    void listerAncres(codeVille).then(setAncres).catch(() => setAncres([]))
    void lireOptionsRecherche(codeVille).then((options) => setBudgets(options.budgetCaps)).catch(() => setBudgets([]))
  }, [villePrete, codeVille])

  // Première page : elle repart de zéro à chaque changement de critère ou de tri. Le curseur d'une
  // recherche précédente n'a aucun sens sur une autre — le serveur le refuse d'ailleurs, son jeton
  // portant l'empreinte des critères.
  useEffect(() => {
    if (!villePrete) {
      return
    }

    let annule = false

    setChargement(true)
    setErreur(null)

    void rechercherAuCurseur(criteres, tri, null, PAR_PAGE)
      .then((reponse) => {
        if (!annule) {
          setVignettes(reponse.items)
          setTotal(reponse.total)
          setCurseur(reponse.nextCursor)
          setPlafondAtteint(reponse.depthCapped)

          // Le serveur dit la ville qu'il a RÉELLEMENT interrogée. Elle diffère de celle demandée
          // quand le code était inconnu ou retiré : l'URL se corrige, l'en-tête dit la vérité.
          if (
            criteres.cityCode !== undefined &&
            reponse.appliedCityCode !== '' &&
            reponse.appliedCityCode !== criteres.cityCode.toUpperCase()
          ) {
            setParams(
              ecrireCriteres(
                { ...criteres, cityCode: reponse.appliedCityCode },
                { sort: params.get('sort') ?? undefined, vue: vueDemandee ?? undefined },
              ),
              { replace: true },
            )
          }
        }
      })
      .catch((cause: Error) => {
        if (!annule) {
          setErreur(phraseDErreur(cause, langue))
        }
      })
      .finally(() => {
        if (!annule) {
          setChargement(false)
        }
      })

    return () => {
      annule = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteres, tri, villePrete])

  /** Page suivante, ajoutée à la liste. Jamais un remplacement : on ne perd pas ce qu'on a lu. */
  const chargerSuite = useCallback(async () => {
    if (curseur === null) {
      return
    }

    setChargement(true)

    try {
      const reponse = await rechercherAuCurseur(criteres, tri, curseur, PAR_PAGE)

      setVignettes((precedentes) => [...precedentes, ...reponse.items])
      setCurseur(reponse.nextCursor)
      setPlafondAtteint(reponse.depthCapped)
    } catch (cause) {
      setErreur(phraseDErreur(cause, langue))
    } finally {
      setChargement(false)
    }
  }, [criteres, tri, curseur, langue])

  // Les punaises ne se chargent que si la carte est à l'écran : le visiteur qui la garde fermée ne
  // paie ni la requête, ni MapLibre.
  useEffect(() => {
    if (!carteVisible || !villePrete) {
      return
    }

    let annule = false

    void rechercherPunaises(criteres)
      .then((reponse) => {
        if (!annule) {
          setPunaises(reponse.pins)
          setTronque(reponse.truncated)
        }
      })
      .catch(() => {
        if (!annule) {
          setPunaises([])
        }
      })

    return () => {
      annule = true
    }
  }, [criteres, carteVisible, villePrete])

  const appliquer = useCallback(
    (nouveaux: Criteres, extra?: Record<string, string | undefined>) => {
      // Tout changement de filtre ramène à la page 1 : rester en page 4 sur un jeu de résultats
      // qui vient de changer affiche une page vide, et l'utilisateur croit qu'il n'y a rien.
      setParams(
        ecrireCriteres(nouveaux, {
          sort: tri,
          vue: vueDemandee ?? undefined,
          page: undefined,
          ...extra,
        }),
      )
    },
    [setParams, tri, vueDemandee],
  )

  /**
   * Tout effacer — les filtres ET le cadrage de la carte (ticket `fo-recherche-08`).
   *
   * Ne retirer que les filtres laissait la carte cadrée sur le dernier déplacement, donc les
   * résultats bornés à un rectangle qu'aucune puce n'affichait plus : on croyait avoir tout effacé
   * et le catalogue restait amputé. Sans bornes, la carte le voit et revient sur le centre de la
   * ville. **La ville, elle, reste** (L13) : ce n'est pas un filtre, c'est là où l'on cherche.
   */
  const toutEffacer = useCallback(
    () => appliquer(criteres.cityCode === undefined ? {} : { cityCode: criteres.cityCode }),
    [appliquer, criteres.cityCode],
  )

  /**
   * Changer de ville depuis cet écran — la feuille de filtres, ou la carte qui dérive.
   *
   * Un choix : mémorisé. Une dérive de carte : pas mémorisée, on explore. Ce qui désignait un lieu
   * dans l'ancienne ville tombe (`pourUneAutreVille`) ; le cadrage est conservé quand c'est lui
   * qui a fait changer de ville, puisque c'est lui qui montre où l'on est.
   */
  const changerDeVille = useCallback(
    (code: string, cadrage?: Partial<Criteres>, memoriser = true) => {
      ville.choisir(code, memoriser ? 'stored' : 'url')
      appliquer({ ...pourUneAutreVille(criteres, code), ...cadrage })
    },
    [ville, appliquer, criteres],
  )

  /**
   * La carte a bougé. Si son centre a quitté la ville et qu'une autre le contient, la ville
   * courante suit (conception L13 §2.6) : l'en-tête change, la pastille aussi, sans requête de
   * plus — la résolution est locale. Le rectangle, lui, prime déjà sur la ville côté serveur.
   */
  const surCadrage = useCallback(
    (cadrage: { south: number; west: number; north: number; east: number }) => {
      const contenant = ville.plusProche((cadrage.south + cadrage.north) / 2, (cadrage.west + cadrage.east) / 2)

      if (contenant !== null && villeCourante !== null && contenant.code !== villeCourante.code) {
        changerDeVille(contenant.code, cadrage, false)
        return
      }

      appliquer({ ...criteres, ...cadrage })
    },
    [ville, villeCourante, changerDeVille, appliquer, criteres],
  )

  // Le chemin de retour après connexion — RELATIF au routeur. `globalThis.location` porte le
  // préfixe de langue, que le routeur ajoute une seconde fois : « /fr/fr/recherche », introuvable.
  const retour = encodeURIComponent(emplacement.pathname + emplacement.search)

  const basculerFavori = useCallback(
    async (id: string, actuel: boolean) => {
      if (!connecte) {
        // On garde l'intention : perdre le geste, c'est perdre le compte. L'annonce visée est
        // reprise après connexion, depuis la page de retour.
        globalThis.sessionStorage.setItem('coloc:favori-en-attente', id)
        navigate(`/connexion?retour=${retour}`)
        return
      }

      // Mise à jour optimiste : sur 3G, attendre l'aller-retour donnerait un cœur qui met une
      // seconde à réagir, et l'utilisateur appuierait deux fois.
      setVignettes((precedentes) =>
        precedentes.map((item) => (item.id === id ? { ...item, isFavorite: !actuel } : item)),
      )

      try {
        await (actuel ? retirerFavori(id) : ajouterFavori(id))
      } catch {
        setVignettes((precedentes) =>
          precedentes.map((item) => (item.id === id ? { ...item, isFavorite: actuel } : item)),
        )
      }
    },
    [connecte, navigate, retour],
  )

  const premiereCharge = (chargement || !villePrete) && vignettes.length === 0
  const quartier = quartiers.find((candidat) => candidat.value === criteres.district)
  const enAmorcage = villeCourante !== null && villeCourante.status === 'Seeding'
  const procheOuverte = enAmorcage ? ville.plusProcheOuverte(villeCourante) : null

  // Y a-t-il une recherche à enregistrer ? Voir le bouton, plus bas : c'est le même seuil que
  // celui de la croix de réinitialisation, et ce n'en est pas un hasard.
  const aQuoiEnregistrer = compterFiltres(criteres) > 0 || aUnCadrage(criteres)

  return (
    <section className="recherche">
      <FenteSousEntete>
        <FiltresRapides
          budgets={budgets}
          criteres={criteres}
          tri={tri}
          onChanger={appliquer}
          onTrier={(valeur) => setParams(ecrireCriteres(criteres, { sort: valeur, vue: vueDemandee ?? undefined }))}
          onOuvrirTout={() => setFeuilleOuverte(true)}
          onToutEffacer={toutEffacer}
        />
      </FenteSousEntete>

      {/*
        L'en-tête de résultats de la maquette : où l'on cherche, combien on a trouvé, et à quelles
        conditions on le lit. « 3 annonces » seul ne disait ni le lieu ni la précision des points
        (ticket `fo-recherche-05`).
      */}
      <div className="recherche__entete">
        <div>
          {/*
            L13 : le <h1> est la VILLE, dans la langue de l'interface, depuis le référentiel — plus
            une clé i18n figée sur le pilote. Le quartier, s'il est posé, descend dans la ligne de
            résultats. « Changer de ville » mène à la feuille ; c'est le geste que D45 promet
            toujours visible.
          */}
          <div className="recherche__ville">
            <h1>{villeCourante === null ? (codeVille ?? '') : libelleVille(villeCourante, langue)}</h1>
            <Link className="puce puce--action" to="/ville">
              <Icone nom="carte" taille="sm" />
              {t.villeChanger}
            </Link>
          </div>
          <p className="recherche__compte" aria-live="polite">
            {premiereCharge ? t.rechercheEnCours : ligneDeResultats(total, criteres, quartier, t, langue)}
          </p>
        </div>

        <div className="recherche__actions">
          {/*
            **Visible même sans compte** — ticket `fo-recherche-10`, première passe. Il ne l'était
            que pour les visiteurs connectés, or le catalogue se consulte sans compte (invariant de
            L3) : le bouton manquait donc exactement à qui n'a pas encore de raison de s'inscrire,
            et enregistrer une recherche en est précisément une.

            Le clic d'un visiteur anonyme mène à la connexion avec l'URL courante en retour. Rien
            n'est à mettre de côté, contrairement au geste du cœur : **la recherche EST l'URL**
            (acquis de L3), donc revenir sur cette adresse après connexion restitue exactement les
            critères, et le bouton ouvre alors le formulaire.

            **Mais grisé tant qu'aucun critère n'est posé** — deuxième passe du même ticket. Le
            catalogue entier n'est pas une recherche : l'enregistrer produirait une ligne sans
            objet dans « mes recherches », et une alerte quotidienne sur toute annonce publiée.
            Grisé plutôt que caché, parce qu'un bouton qui apparaît en cours de route ne se
            remarque pas — celui-là dit ce qu'il attend, et devient cliquable dès qu'on le lui
            donne.

            Le seuil est le MÊME que celui de la croix de réinitialisation : dès qu'il y a quelque
            chose à annuler, il y a quelque chose à enregistrer. Un cadrage de carte compte, alors
            qu'il n'est pas compté comme un filtre — c'est bien un critère, il borne les résultats
            et il repart dans l'URL.
          */}
          {connecte ? (
            <EnregistrerRecherche
              criteres={criteres}
              desactive={!aQuoiEnregistrer}
              raisonDesactive={t.rechercheRienAEnregistrer}
              suggestion={suggererNom(
                criteres,
                quartiers,
                villeCourante === null ? null : libelleVille(villeCourante, langue),
                t.rechercheChercher,
                t.rechercheJusqua,
              )}
              onEnregistree={() => navigate('/mes-recherches')}
            />
          ) : (
            <button
              type="button"
              className="puce puce--action"
              disabled={!aQuoiEnregistrer}
              title={aQuoiEnregistrer ? undefined : t.rechercheRienAEnregistrer}
              onClick={() => navigate(`/connexion?retour=${retour}`)}
            >
              <Icone nom="signet" taille="sm" />
              {t.suiviEnregistrerRecherche}
            </button>
          )}

          {/*
            Un seul bouton, et pas deux segments : la liste est toujours là, donc « Liste » ne
            mène nulle part. Ce qui se décide ici est la présence de la carte, ce que le libellé
            dit maintenant en toutes lettres.
          */}
          <button
            type="button"
            className="puce puce--action"
            aria-pressed={carteVisible}
            onClick={() =>
              setParams(ecrireCriteres(criteres, { sort: tri, vue: carteVisible ? 'liste' : 'carte' }))
            }
          >
            <Icone nom="carte" taille="sm" />
            {carteVisible ? t.rechercheMasquerCarte : t.rechercheAfficherCarte}
          </button>
        </div>
      </div>

      {erreur !== null && <p className="erreur">{erreur}</p>}

      <div className={`recherche__corps recherche__corps--${carteVisible ? 'carte' : 'liste'}`}>
        <div className="recherche__liste">
          {/*
            La ville n'est pas encore ouverte (D44, §2.10) : on le dit AVANT les résultats, avec la
            ville ouverte la plus proche et « Prévenez-moi ». Les résultats suivent quand même — des
            propriétaires peuvent déjà y publier — et l'état vide générique se tait, puisque ce
            bloc dit déjà pourquoi il n'y a rien.
          */}
          {enAmorcage && villeCourante !== null && (
            <section className="bloc recherche__amorcage">
              <div className="bloc__entete">
                <h2>{interpoler(t.villePasEncoreTitre, { marque: t.titre, ville: libelleVille(villeCourante, langue) })}</h2>
                {(ville.source === 'device' || ville.source === 'ip') && (
                  <Puce ton="attention">{t.villeDetectee}</Puce>
                )}
              </div>
              <p className="secondaire">
                {procheOuverte === null
                  ? t.villePasEncoreAideSeule
                  : interpoler(
                      t.villePasEncoreAide,
                      { ville: libelleVille(procheOuverte.ville, langue), km: Math.round(procheOuverte.km) },
                      langue,
                    )}
              </p>
              <div className="rangee rangee--serree ville-actions">
                <PrevenezMoi ville={villeCourante} connecte={connecte} />
                {procheOuverte !== null && (
                  <Bouton apparence="secondaire" onClick={() => changerDeVille(procheOuverte.ville.code)}>
                    {interpoler(t.villeChercherA, { ville: libelleVille(procheOuverte.ville, langue) })}
                  </Bouton>
                )}
              </div>
            </section>
          )}

          {/*
            Squelettes, jamais « Recherche… » à la place des résultats : ils occupent exactement
            la place des cartes, donc rien ne bouge quand la réponse arrive.
          */}
          {premiereCharge && (
            <ul className="grille-annonces">
              {Array.from({ length: 6 }, (_, rang) => (
                <VignetteSquelette key={rang} />
              ))}
            </ul>
          )}

          {!premiereCharge && vignettes.length === 0 && !enAmorcage && (
            <EtatVide
              icone="loupe"
              titre={t.rechercheAucuneTitre}
              action={
                aDesFiltres(criteres) ? (
                  <Bouton apparence="secondaire" onClick={toutEffacer}>
                    {t.rechercheEffacerFiltres}
                  </Bouton>
                ) : undefined
              }
            >
              {t.rechercheAucuneAide}
            </EtatVide>
          )}

          {vignettes.length > 0 && (
            <ul className="grille-annonces">
              {vignettes.map((annonce) => (
                <Vignette
                  key={annonce.id}
                  annonce={annonce}
                  recommandee={recommandees.get(annonce.id) ?? null}
                  onFavori={basculerFavori}
                  onSurvol={setSurvolee}
                />
              ))}
            </ul>
          )}

          {/*
            L9 : « voir plus » plutôt qu'une numérotation de pages.

            Le curseur rend le RANG non adressable — et c'est ce qui le rend stable. Avec un
            `OFFSET`, une annonce publiée pendant la consultation décale toutes les pages
            suivantes : un résultat déjà vu réapparaît pendant qu'un autre disparaît sans avoir
            été lu.

            Les FILTRES restent dans l'URL, et c'est eux que porte l'acquis de L3 : ce qu'on
            partage est une recherche, jamais le rang auquel on s'était arrêté.
          */}
          {/*
            **« 6 annonces sur 47 », à côté du bouton** — ticket `fo-recherche-11`.

            Le bouton seul ne disait pas où l'on en était : on voyait six cartes, un bouton, et
            aucun moyen de savoir s'il restait un résultat ou quarante. La ligne d'en-tête donne
            le total, mais pas ce qui est déjà chargé — et c'est l'écart entre les deux qui
            renseigne. C'est aussi ce que montre la maquette, au même endroit.
          */}
          {curseur !== null && (
            <div className="recherche__plus">
              <p className="recherche__avancement" aria-live="polite">
                {`${nombre(vignettes.length, langue)} ${
                  vignettes.length > 1 ? t.rechercheAnnonces : t.rechercheAnnonce
                } ${t.rechercheSurUnTotalDe} ${nombre(total, langue)}`}
              </p>
              <Bouton
                apparence="secondaire"
                charge={chargement && vignettes.length > 0}
                onClick={() => void chargerSuite()}
              >
                {t.recherchePlusDeResultats}
              </Bouton>
            </div>
          )}

          {plafondAtteint && (
            <p className="recherche__tronque" role="status">
              {t.recherchePlafondAtteint}
            </p>
          )}
        </div>

        {carteVisible && (
          <div className="recherche__carte">
            {tronque && (
              <p className="recherche__tronque" role="status">
                {t.rechercheTropDAnnonces}
              </p>
            )}
            <CarteRecherche
              punaises={punaises}
              selection={survolee}
              centre={villeCourante === null ? undefined : villeCourante}
              cadrage={
                criteres.south !== undefined &&
                criteres.west !== undefined &&
                criteres.north !== undefined &&
                criteres.east !== undefined
                  ? {
                      south: criteres.south,
                      west: criteres.west,
                      north: criteres.north,
                      east: criteres.east,
                    }
                  : undefined
              }
              onCadrageChange={surCadrage}
              onOuvrir={(id) => navigate(`/annonces/${id}`)}
            />
          </div>
        )}
      </div>

      {feuilleOuverte && (
        <Modale
          titre={t.rechercheFiltres}
          feuille
          onFermer={() => setFeuilleOuverte(false)}
          pied={
            <>
              <Bouton apparence="discret" onClick={toutEffacer}>
                {t.rechercheReinitialiser}
              </Bouton>
              <Bouton onClick={() => setFeuilleOuverte(false)}>
                {premiereCharge
                  ? t.rechercheAppliquer
                  : `${t.rechercheAppliquer} (${nombre(total, langue)})`}
              </Bouton>
            </>
          }
        >
          <Filtres
            criteres={criteres}
            quartiers={quartiers}
            ancres={ancres}
            villes={ville.villes}
            villeCourante={villeCourante}
            onChanger={appliquer}
            onChangerDeVille={(code) => changerDeVille(code)}
          />
        </Modale>
      )}
    </section>
  )
}

/**
 * La fente de sous-en-tête — un portail vers la barre pleine largeur posée par la coquille.
 *
 * **Pourquoi un portail** (ticket `fo-recherche-01`). La barre de filtres doit avoir un fond qui
 * va d'un bord à l'autre de la fenêtre, comme dans la maquette. Or elle est rendue par l'écran de
 * recherche, qui vit à l'intérieur d'une coquille bornée et centrée : depuis là, aucune règle CSS
 * honnête ne donne la pleine largeur. Les recettes à base de `margin-inline: calc(50% - 50vw)`
 * fonctionnent jusqu'au jour où une barre de défilement apparaît, et produisent alors un
 * débordement horizontal — c'est-à-dire le défaut qu'on vient corriger, dans l'autre sens.
 *
 * Le portail règle la question à la racine : le markup est rendu là où il doit être dans le
 * document, et l'état des filtres reste où il doit être dans le code.
 *
 * Le nœud cible n'existe qu'après le premier rendu de la coquille, d'où le passage par un état :
 * un `document.getElementById` évalué pendant le rendu rendrait `null` à la première passe.
 */
function FenteSousEntete({ children }: { children: React.ReactNode }) {
  const [fente, setFente] = useState<HTMLElement | null>(null)

  useEffect(() => setFente(document.getElementById('sous-entete')), [])

  return fente === null ? null : createPortal(children, fente)
}

/**
 * La ligne de résultats : combien, où, et à quelle précision.
 *
 * Elle ne parle jamais de rayon, contrairement à la maquette : la recherche du pilote n'en a pas.
 * Ce qui borne les résultats est soit un quartier, soit le cadrage de la carte, soit rien — et
 * c'est cela qu'on écrit. Afficher « rayon 3 km » parce que la maquette le montre reviendrait à
 * inventer une garantie que le moteur ne donne pas.
 *
 * Le quartier y descend depuis L13 : le <h1> est désormais la ville.
 */
function ligneDeResultats(
  total: number,
  criteres: Criteres,
  quartier: Quartier | undefined,
  t: ReturnType<typeof useTextes>,
  langue: 'fr' | 'ar' | 'en',
): string {
  const morceaux = [
    `${nombre(total, langue)} ${total > 1 ? t.rechercheLogements : t.rechercheLogement}`,
  ]

  if (criteres.district !== undefined) {
    morceaux.push(quartier?.label ?? criteres.district)
  }

  if (criteres.south !== undefined) {
    morceaux.push(t.rechercheZoneCarte)
  }

  morceaux.push(t.recherchePositionApprochee)

  return morceaux.join(' · ')
}

/**
 * La rangée de filtres rapides.
 *
 * Trois réglages sur six représentent l'essentiel des usages : le type de logement, le budget, et
 * « vérifiée sur place ». Ils sont ici, à un geste. Le reste — quartier, date, proximité, texte —
 * est derrière « Filtres », parce qu'on ne les touche qu'une fois par recherche.
 *
 * La rangée défile horizontalement **dans son propre conteneur** : elle ne fait jamais défiler la
 * page, quelle que soit la largeur. Le tri ferme la marche, calé sur le bord de fin — c'est le
 * seul contrôle de la rangée qui ne soit pas un filtre, et le mettre à part le dit sans légende.
 */
function FiltresRapides({
  budgets,
  criteres,
  tri,
  onChanger,
  onTrier,
  onOuvrirTout,
  onToutEffacer,
}: {
  budgets: number[]
  criteres: Criteres
  tri: Tri
  onChanger: (criteres: Criteres) => void
  onTrier: (tri: string) => void
  onOuvrirTout: () => void
  onToutEffacer: () => void
}) {
  const t = useTextes()
  const langue = useLangue()
  const actifs = compterFiltres(criteres)

  /*
    **La croix apparaît aussi quand c'est la CARTE qui filtre** — ticket `fo-recherche-12`.

    Déplacer ou zoomer la carte écrit des bornes dans l'URL, donc restreint les résultats. Mais
    le cadrage n'est pas compté comme un filtre — et c'est juste : il n'a pas été posé dans la
    feuille, il ne s'y retrouve pas, et le faire figurer dans le compteur ferait dire « 3 filtres »
    à quelqu'un qui n'en a coché qu'un. Il restait donc un état où les résultats étaient bornés
    sans qu'aucun bouton ne permette de revenir en arrière autrement qu'en dézoomant à la main.

    La croix répond à « comment j'annule ? », pas à « combien en ai-je posé ? ». Elle s'affiche
    dès qu'il y a quelque chose à annuler, filtres ou cadrage ; le compteur, lui, continue de ne
    compter que ce qui a été coché.
  */
  const aQuoiRevenir = actifs > 0 || aUnCadrage(criteres)

  const typePuce = (valeur: Criteres['unitKind'], libelle: string) => (
    <button
      type="button"
      className="puce puce--action"
      aria-pressed={criteres.unitKind === valeur}
      onClick={() =>
        onChanger({ ...criteres, unitKind: criteres.unitKind === valeur ? undefined : valeur })
      }
    >
      {libelle}
    </button>
  )

  return (
    <div className="filtres-rapides">
      <div className="filtres-rapides__piste">
        {/*
          « Filtres » et sa croix sont une seule pastille en deux moitiés (ticket
          `fo-recherche-07`). Deux boutons voisins auraient l'air de deux filtres ; une croix
          collée au compteur se lit comme « annuler ceci », qui est exactement ce qu'elle fait.
          Elle n'apparaît que s'il y a quelque chose à retirer.
        */}
        <span className={aQuoiRevenir ? 'puce puce--duo est-actif' : 'puce puce--duo'}>
          <button type="button" className="puce__moitie" onClick={onOuvrirTout}>
            <Icone nom="filtre" taille="sm" />
            {t.rechercheFiltres}
            {actifs > 0 && <span className="compteur">{nombre(actifs, langue)}</span>}
          </button>

          {aQuoiRevenir && (
            <button
              type="button"
              className="puce__croix"
              aria-label={t.rechercheEffacerFiltres}
              onClick={onToutEffacer}
            >
              <Icone nom="croix" taille="sm" />
            </button>
          )}
        </span>

        {typePuce('PrivateRoom', t.typeChambrePrivee)}
        {typePuce('EntireProperty', t.typeLogementEntier)}
        {typePuce('SharedRoom', t.typeChambrePartagee)}

        {budgets.map((plafond) => (
          <button
            key={plafond}
            type="button"
            className="puce puce--action"
            aria-pressed={criteres.maxRent === plafond}
            onClick={() =>
              onChanger({ ...criteres, maxRent: criteres.maxRent === plafond ? undefined : plafond })
            }
          >
            {t.rechercheJusqua} <bdi>{montant(plafond, 'MAD', langue)}</bdi>
          </button>
        ))}

        <button
          type="button"
          className="puce puce--action"
          aria-pressed={criteres.verifiedOnly === true}
          onClick={() =>
            onChanger({ ...criteres, verifiedOnly: criteres.verifiedOnly === true ? undefined : true })
          }
        >
          <Icone nom="verifie" taille="sm" />
          {t.badgeVerifiee}
        </button>

        <label className="puce puce--tri">
          <Icone nom="tri" taille="sm" />
          <span className="puce--tri__libelle">{t.rechercheTrier}</span>
          <select value={tri} onChange={(evenement) => onTrier(evenement.target.value)}>
            <option value="Relevance">{t.rechercheTriPertinence}</option>
            <option value="PriceAscending">{t.rechercheTriPrixCroissant}</option>
            <option value="PriceDescending">{t.rechercheTriPrixDecroissant}</option>
            <option value="Newest">{t.rechercheTriRecentes}</option>
          </select>
        </label>
      </div>
    </div>
  )
}

/** Le nombre de filtres réellement posés — le cadrage de la carte n'en est pas un. */
function compterFiltres(criteres: Criteres): number {
  return [
    criteres.text,
    criteres.district,
    criteres.minRent,
    criteres.maxRent,
    criteres.unitKind,
    criteres.availableFrom,
    criteres.anchorCode,
    criteres.maxTravelMinutes,
    criteres.furnished,
    criteres.verifiedOnly,
    criteres.withOriginalPhotosOnly,
    criteres.amenityCodes?.length === 0 ? undefined : criteres.amenityCodes,
  ].filter((valeur) => valeur !== undefined).length
}

/**
 * Le formulaire complet, dans la feuille.
 *
 * **Le quartier est une LISTE, jamais une saisie.** C'est ce qui rend la saisie libre exploitable
 * (conception L3, §1.1) : on ne propose que ce que le catalogue contient réellement, et la valeur
 * envoyée est la forme normalisée. Demander à quelqu'un de taper « Hay Al Wifaq » à l'identique
 * rendrait zéro une fois sur trois.
 *
 * **Recette du 08/09** (ticket `fo-recherche-02`), quatre écarts avec la maquette, corrigés ici :
 * le type de logement redevient une rangée de puces — trois choix se montrent, ils ne se déroulent
 * pas ; le budget porte ses deux bornes et non le seul plafond ; « à proximité de » retrouve la
 * durée à côté du point de repère ; et le bloc « Équipements et garanties » existe, alors que les
 * critères correspondants étaient acceptés par l'API depuis L3 sans que rien ne permette de les
 * poser.
 */
function Filtres({
  criteres,
  quartiers,
  ancres,
  villes,
  villeCourante,
  onChanger,
  onChangerDeVille,
}: {
  criteres: Criteres
  quartiers: Quartier[]
  ancres: Ancre[]
  villes: Ville[]
  villeCourante: Ville | null
  onChanger: (criteres: Criteres) => void
  onChangerDeVille: (code: string) => void
}) {
  const t = useTextes()
  const langue = useLangue()

  // Les villes OUVERTES, avec leur nombre d'annonces — jamais une saisie (D44). La ville courante
  // y figure même en amorçage, sans quoi la liste ne saurait pas l'afficher comme choisie.
  const villesProposees = villes.filter(
    (candidate) => candidate.status === 'Open' || candidate.code === villeCourante?.code,
  )

  const [texte, setTexte] = useState(criteres.text ?? '')

  useEffect(() => setTexte(criteres.text ?? ''), [criteres.text])

  const equipementsPoses = criteres.amenityCodes ?? []

  const basculerEquipement = (code: string, actif: boolean) => {
    const suivants = actif
      ? [...equipementsPoses, code]
      : equipementsPoses.filter((candidat) => candidat !== code)

    onChanger({ ...criteres, amenityCodes: suivants.length > 0 ? suivants : undefined })
  }

  return (
    <form
      className="filtres"
      onSubmit={(evenement) => {
        evenement.preventDefault()
        onChanger({ ...criteres, text: texte || undefined })
      }}
    >
      {/*
        La ville en PREMIER critère — ticket `fo-recherche-03`, soldé par L13. Une liste des villes
        ouvertes avec leur nombre d'annonces ; « choisir sa ville » mène à la feuille, pour les
        autres villes et pour « autour de moi ».
      */}
      {villesProposees.length > 0 && (
        <p className="champ">
          <label htmlFor="filtre-ville">{t.villeFiltre}</label>
          <select
            id="filtre-ville"
            value={villeCourante?.code ?? ''}
            onChange={(evenement) => {
              if (evenement.target.value !== '' && evenement.target.value !== villeCourante?.code) {
                onChangerDeVille(evenement.target.value)
              }
            }}
          >
            {villesProposees.map((candidate) => (
              <option key={candidate.code} value={candidate.code}>
                {candidate.status === 'Open'
                  ? `${libelleVille(candidate, langue)} · ${nombre(candidate.publishedListingCount, langue)}`
                  : `${libelleVille(candidate, langue)} · ${t.villePasEncoreOuverte}`}
              </option>
            ))}
          </select>
          <span className="champ__aide">
            {t.villeFiltreAide} <Link to="/ville">{t.villeChoisirLien}</Link>
          </span>
        </p>
      )}

      <p className="champ">
        <label htmlFor="filtre-texte">{t.rechercheChercher}</label>
        <input
          id="filtre-texte"
          type="search"
          value={texte}
          onChange={(evenement) => setTexte(evenement.target.value)}
          onBlur={() => onChanger({ ...criteres, text: texte || undefined })}
          placeholder={t.recherchePlaceholder}
        />
      </p>

      <div className="champ">
        <span className="champ__libelle">{t.rechercheType}</span>
        <div className="rangee rangee--serree">
          {(
            [
              ['PrivateRoom', t.typeChambrePrivee],
              ['EntireProperty', t.typeLogementEntier],
              ['SharedRoom', t.typeChambrePartagee],
            ] as const
          ).map(([valeur, libelle]) => (
            <button
              key={valeur}
              type="button"
              className="puce puce--action"
              aria-pressed={criteres.unitKind === valeur}
              onClick={() =>
                onChanger({
                  ...criteres,
                  unitKind: criteres.unitKind === valeur ? undefined : valeur,
                })
              }
            >
              {libelle}
            </button>
          ))}
        </div>
      </div>

      <div className="champ">
        <span className="champ__libelle">{t.rechercheBudgetMensuel}</span>
        <span className="champ__groupe">
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={100}
            aria-label={t.rechercheMinimum}
            placeholder={t.rechercheMinimum}
            value={criteres.minRent ?? ''}
            onChange={(evenement) =>
              onChanger({
                ...criteres,
                minRent: evenement.target.value ? Number(evenement.target.value) : undefined,
              })
            }
          />
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={100}
            aria-label={t.rechercheMaximum}
            placeholder={t.rechercheMaximum}
            value={criteres.maxRent ?? ''}
            onChange={(evenement) =>
              onChanger({
                ...criteres,
                maxRent: evenement.target.value ? Number(evenement.target.value) : undefined,
              })
            }
          />
          <span className="champ__suffixe">MAD</span>
        </span>
      </div>

      <p className="champ">
        <label htmlFor="filtre-quartier">{t.rechercheQuartier}</label>
        <select
          id="filtre-quartier"
          value={criteres.district ?? ''}
          onChange={(evenement) =>
            onChanger({ ...criteres, district: evenement.target.value || undefined })
          }
        >
          <option value="">{t.rechercheTous}</option>
          {quartiers.map((quartier) => (
            <option key={quartier.value} value={quartier.value}>
              {quartier.label} ({nombre(quartier.listingCount, langue)})
            </option>
          ))}
        </select>
      </p>

      <p className="champ">
        <label htmlFor="filtre-date">{t.rechercheDisponibleAvant}</label>
        <input
          id="filtre-date"
          type="date"
          value={criteres.availableFrom ?? ''}
          onChange={(evenement) =>
            onChanger({ ...criteres, availableFrom: evenement.target.value || undefined })
          }
        />
      </p>

      {ancres.length > 0 && (
        <div className="champ">
          <span className="champ__libelle">{t.rechercheAProximite}</span>
          {/*
            Le repère et la durée sur une seule ligne, comme dans la maquette : ce sont les deux
            moitiés d'une même phrase — « près de X, en moins de Y ». Séparées en deux champs
            empilés, on pouvait poser l'une sans l'autre et obtenir un filtre sans effet.
          */}
          <span className="champ__groupe">
            <select
              aria-label={t.rechercheAProximite}
              value={criteres.anchorCode ?? ''}
              onChange={(evenement) =>
                onChanger({ ...criteres, anchorCode: evenement.target.value || undefined })
              }
            >
              <option value="">{t.recherchePeuImporte}</option>
              {ancres.map((ancre) => (
                <option key={ancre.code} value={ancre.code}>
                  {libelleAncre(ancre, langue)}
                </option>
              ))}
            </select>
            <select
              aria-label={t.rechercheDureeMax}
              value={criteres.maxTravelMinutes ?? ''}
              onChange={(evenement) =>
                onChanger({
                  ...criteres,
                  maxTravelMinutes: evenement.target.value
                    ? Number(evenement.target.value)
                    : undefined,
                })
              }
            >
              <option value="">—</option>
              <option value="10">{`${t.rechercheEnMoinsDe} 10 min`}</option>
              <option value="20">{`${t.rechercheEnMoinsDe} 20 min`}</option>
              <option value="30">{`${t.rechercheEnMoinsDe} 30 min`}</option>
            </select>
          </span>
          <span className="aide">{t.rechercheTrajetEstime}</span>
        </div>
      )}

      {/*
        Équipements ET garanties dans le même bloc, et ce n'est pas un raccourci de mise en page :
        du point de vue de qui cherche, « meublé » et « vérifiée sur place » répondent à la même
        question — qu'est-ce que je trouve en arrivant. Les deux badges portent leur origine en
        toutes lettres, parce que l'invariant 7 les sépare et qu'un lecteur doit pouvoir savoir
        lequel est automatique et lequel engage l'équipe.
      */}
      <fieldset className="filtres__equipements">
        <legend className="champ__libelle">{t.rechercheEquipementsEtGaranties}</legend>

        <label className="case">
          <input
            type="checkbox"
            checked={criteres.furnished === true}
            onChange={(evenement) =>
              onChanger({ ...criteres, furnished: evenement.target.checked || undefined })
            }
          />
          {t.publicationMeuble}
        </label>

        {EQUIPEMENTS.map((equipement) => (
          <label key={equipement.code} className="case">
            <input
              type="checkbox"
              checked={equipementsPoses.includes(equipement.code)}
              onChange={(evenement) => basculerEquipement(equipement.code, evenement.target.checked)}
            />
            {t[equipement.libelle]}
          </label>
        ))}

        <label className="case">
          <input
            type="checkbox"
            checked={criteres.withOriginalPhotosOnly === true}
            onChange={(evenement) =>
              onChanger({
                ...criteres,
                withOriginalPhotosOnly: evenement.target.checked || undefined,
              })
            }
          />
          {`${t.badgePhotos} (${t.badgePhotosAuto})`}
        </label>

        <label className="case">
          <input
            type="checkbox"
            checked={criteres.verifiedOnly === true}
            onChange={(evenement) =>
              onChanger({ ...criteres, verifiedOnly: evenement.target.checked || undefined })
            }
          />
          {`${t.badgeVerifiee} (${t.badgeVerifieeAdmin})`}
        </label>
      </fieldset>
    </form>
  )
}

/** Le libellé d'une ancre, dans la langue courante. Les trois sont portées par le référentiel. */
function libelleAncre(ancre: Ancre, langue: 'fr' | 'ar' | 'en'): string {
  if (langue === 'ar') {
    return ancre.labelAr
  }

  return langue === 'en' ? ancre.labelEn : ancre.labelFr
}

/**
 * Nom pre-rempli d'une recherche a enregistrer.
 *
 * Demander de saisir un nom avant d'avoir vu les resultats est le meilleur moyen de n'en faire
 * enregistrer aucune. Un nom compose des criteres est presque toujours celui qu'on aurait ecrit.
 */
function suggererNom(
  criteres: Criteres,
  quartiers: Quartier[],
  ville: string | null,
  defaut: string,
  jusqua: string,
): string {
  const morceaux: string[] = []

  if (criteres.unitKind === 'PrivateRoom' || criteres.unitKind === 'SharedRoom') {
    morceaux.push('Chambre')
  } else if (criteres.unitKind === 'EntireProperty') {
    morceaux.push('Logement')
  } else {
    morceaux.push(defaut)
  }

  const quartier = quartiers.find((candidat) => candidat.value === criteres.district)

  // Le quartier quand il y en a un, la ville sinon : « Chambre · Hay Al Wifaq » se comprend seul
  // à Salé, mais « Chambre · Casablanca » distingue deux recherches de deux villes.
  if (quartier !== undefined) {
    morceaux.push(quartier.label)
  } else if (ville !== null) {
    morceaux.push(ville)
  }

  if (criteres.maxRent !== undefined) {
    morceaux.push(`${jusqua} ${criteres.maxRent}`)
  }

  return morceaux.join(' · ').slice(0, 80)
}
